import React, { useState } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Image, SafeAreaView, ActivityIndicator, Platform } from "react-native";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const SignUp = () => {
	const [step, setStep] = useState(1);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const { signUp } = useAuth();
	const navigate = useNavigate();

	const handleBack = () => {
		if (step === 2) {
			setStep(1);
		} else {
			navigate("/login");
		}
	};

	const handleNext = async () => {
		setError("");

		if (step === 1) {
			if (!name || !email) {
				setError("Please fill in all fields");
				return;
			}
			// Basic email validation
			if (!/\S+@\S+\.\S+/.test(email)) {
				setError("Please enter a valid email address");
				return;
			}
			setStep(2);
			return;
		}

		if (step === 2) {
			if (!password) {
				setError("Please enter a password");
				return;
			}
			// Password validation
			if (password.length < 10) {
				setError("Password must be at least 10 characters long");
				return;
			}
			if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
				setError("Password must contain both upper and lower case letters");
				return;
			}
			if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
				setError("Password must contain at least one symbol");
				return;
			}

			setLoading(true);
			try {
				const success = await signUp(email, password, name);
				if (success) {
					navigate("/two-factor", { state: { email } });
				} else {
					setError("Failed to create account. Please try again.");
				}
			} catch (err) {
				setError(err.message || "An error occurred during sign up");
			} finally {
				setLoading(false);
			}
		}
	};

	return (
		<SafeAreaView style={styles.container}>
			<View style={styles.content}>
				{/* Logo */}
				<View style={styles.logoContainer}>
					<View style={styles.logoBox}>
						<Image source={require("../assets/icon.png")} style={styles.logo} resizeMode="contain" />
					</View>
				</View>

				{/* Welcome Text */}
				<View style={styles.welcomeContainer}>
					<Text style={styles.welcomeTitle}>Welcome to Executable!</Text>
					<Text style={styles.welcomeSubtitle}>Sign up to start making your idea a reality</Text>
					{error ? <Text style={styles.errorText}>{error}</Text> : null}
				</View>

				{/* Back Button */}
				<TouchableOpacity style={styles.backButton} onPress={handleBack}>
					<Text style={styles.backButtonText}>← Back</Text>
				</TouchableOpacity>

				{/* Form */}
				<View style={styles.form}>
					{step === 1 ? (
						<>
							<View style={styles.inputContainer}>
								<Text style={styles.label}>Name</Text>
								<TextInput style={styles.input} placeholder="Enter your name" placeholderTextColor="#666" value={name} onChangeText={setName} editable={!loading} />
							</View>

							<View style={styles.inputContainer}>
								<Text style={styles.label}>Email</Text>
								<TextInput style={styles.input} placeholder="Enter your email" placeholderTextColor="#666" value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" editable={!loading} />
							</View>
						</>
					) : (
						<View style={styles.inputContainer}>
							<Text style={styles.label}>Password</Text>
							<TextInput style={styles.input} placeholder="Create new password" placeholderTextColor="#666" value={password} onChangeText={setPassword} secureTextEntry editable={!loading} />
							<View style={styles.passwordRequirements}>
								<Text style={styles.requirementText}>• Upper & lower case letters</Text>
								<Text style={styles.requirementText}>• A symbol (#$%)</Text>
								<Text style={styles.requirementText}>• A minimum of 10 characters</Text>
							</View>
						</View>
					)}

					<TouchableOpacity style={[styles.signUpButton, loading && styles.buttonDisabled]} onPress={handleNext} disabled={loading}>
						{loading ? <ActivityIndicator color="white" /> : <Text style={styles.signUpButtonText}>{step === 1 ? "Next" : "Create Account"}</Text>}
					</TouchableOpacity>

					{false && (
						<TouchableOpacity style={[styles.googleButton, loading && styles.buttonDisabled]} disabled={loading}>
							<Image
								source={{
									uri: "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg",
								}}
								style={styles.googleIcon}
							/>
							<Text style={styles.googleButtonText}>Sign in with Google</Text>
						</TouchableOpacity>
					)}
				</View>

				<View style={styles.termsContainer}>
					<Text style={styles.termsText}>
						Signing up for a Executable account means you agree to the{" "}
						<Text style={styles.termsLink} onPress={() => {}}>
							Privacy Policy
						</Text>{" "}
						and{" "}
						<Text style={styles.termsLink} onPress={() => {}}>
							Terms of Service
						</Text>
					</Text>
				</View>
			</View>
		</SafeAreaView>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#000000",
	},
	content: {
		flex: 1,
		padding: 24,
		justifyContent: "center",
		maxWidth: 400,
		alignSelf: "center",
		width: "100%",
	},
	logoContainer: {
		alignItems: "center",
		marginBottom: 32,
	},
	logoBox: {
		width: 200,
		height: 48,
		backgroundColor: "transparent",
		borderRadius: 12,
		justifyContent: "center",
		alignItems: "center",
	},
	logo: {
		width: 150,
		height: 150,
		shadowColor: "#fff",
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.9,
		shadowRadius: 10,
		elevation: 10,
		...(Platform.OS === "web" ? { boxShadow: "0 0 10px 10px #ffffff" } : {}),
	},
	welcomeContainer: {
		alignItems: "center",
		marginBottom: 32,
	},
	welcomeTitle: {
		fontSize: 24,
		fontWeight: "bold",
		color: "white",
		marginBottom: 8,
	},
	welcomeSubtitle: {
		fontSize: 16,
		color: "#666",
	},
	backButton: {
		position: "absolute",
		top: 24,
		left: 24,
	},
	backButtonText: {
		color: "#666",
		fontSize: 16,
	},
	form: {
		gap: 16,
	},
	inputContainer: {
		marginBottom: 16,
	},
	label: {
		color: "#666",
		marginBottom: 8,
		fontSize: 14,
	},
	input: {
		backgroundColor: "#1A1A1A",
		borderWidth: 1,
		borderColor: "#333",
		borderRadius: 6,
		padding: 12,
		color: "white",
		fontSize: 14,
	},
	passwordRequirements: {
		marginTop: 12,
	},
	requirementText: {
		color: "#666",
		fontSize: 14,
		marginBottom: 4,
	},
	signUpButton: {
		backgroundColor: "#0066FF",
		padding: 16,
		borderRadius: 6,
		alignItems: "center",
		marginBottom: 16,
	},
	signUpButtonText: {
		color: "white",
		fontSize: 14,
		fontWeight: "500",
	},
	googleButton: {
		backgroundColor: "white",
		padding: 16,
		borderRadius: 6,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
	},
	googleIcon: {
		width: 20,
		height: 20,
		marginRight: 8,
	},
	googleButtonText: {
		color: "black",
		fontSize: 14,
		fontWeight: "500",
	},
	termsContainer: {
		marginTop: 32,
	},
	termsText: {
		color: "#666",
		fontSize: 14,
		textAlign: "center",
	},
	termsLink: {
		color: "#0066FF",
	},
	errorText: {
		color: "#ff4444",
		marginTop: 8,
		fontSize: 14,
		textAlign: "center",
	},
	buttonDisabled: {
		opacity: 0.7,
	},
});

export default SignUp;
